import { useEffect, useState } from "react";
import {
  addPubOwnerApi,
  PubOwnerDomain,
  PubOwnerResponse,
  PubOwnerRole,
  updatePubOwnerApi,
} from "../../../../api/cafe";
import * as S from "./style";
import MemberSearch from "../search";
import { pubState } from "../../../../recoil/pub.atom";
import { useRecoilValue } from "recoil";
import DropBox from "../../../../component/DropBox";
import {
  isPubOwner,
  domainOptions,
  roleOptions,
  fromApiDomains,
  getSelectableDomains,
  isDomainChecked,
  toApiDomains,
} from "../../../../utils/owner";

export interface OwnerEditProps {
  onClose: () => void;
  owner?: PubOwnerResponse | null;
  getData: () => void;
}

const OwnerEdit = ({ onClose, owner, getData }: OwnerEditProps) => {
  const pub = useRecoilValue(pubState);
  const [selectedUser, setSelectedUser] = useState<{
    id: number;
    nickname: string;
  } | null>(null);
  const [isSearch, setIsSearch] = useState<boolean>(false);
  const [role, setRole] = useState<PubOwnerRole>(PubOwnerRole.STAFF);
  const [tournamentIds, setTournamentIds] = useState<string>("");
  const [isAllDomains, setIsAllDomains] = useState<boolean>(false);
  const [selectedDomains, setSelectedDomains] = useState<PubOwnerDomain[]>([]);
  const [isDropbox, setIsDropbox] = useState<number>(-1);

  const handleSubmit = async () => {
    if (!pub || !selectedUser) return;

    const requestDomains = toApiDomains(isAllDomains, selectedDomains);

    const requestRole =
      requestDomains.length === 0 && role === PubOwnerRole.MANAGER
        ? PubOwnerRole.OWNER
        : role;

    const request = {
      pubId: pub.id,
      userId: selectedUser.id,
      role: requestRole,
      tournamentIds:
        role === PubOwnerRole.STAFF
          ? tournamentIds
              .split(",")
              .map((id) => Number(id.trim()))
              .filter((id) => id > 0)
          : [],
      permissions: [],
      domains: requestDomains,
    };

    try {
      if (owner) {
        await updatePubOwnerApi(owner.id, request);
        alert("유저 권한 수정이 완료되었습니다.");
      } else {
        await addPubOwnerApi(request);
        alert("유저 권한 추가가 완료되었습니다.");
      }

      onClose();
      getData();
    } catch (e: any) {
      alert(e.message);
      console.log(e);
    }
  };

  useEffect(() => {
    if (!owner) return;
    setSelectedUser({ id: owner.userId, nickname: owner.nickname });
    setRole(
      isPubOwner(owner.role, owner.domains) ? PubOwnerRole.MANAGER : owner.role,
    );
    setTournamentIds(owner.tournamentIds.join(","));
    const { isAllDomains, selectedDomains } = fromApiDomains(owner.domains);
    setIsAllDomains(isAllDomains);
    setSelectedDomains(selectedDomains);
  }, [owner]);

  const handleSelectDomain = (value: PubOwnerDomain | null) => {
    const allDomains = getSelectableDomains();
    const isEveryDomainSelected =
      selectedDomains.length > 0 &&
      allDomains.every((domain) => selectedDomains.includes(domain));
    const isAll = isAllDomains || isEveryDomainSelected;

    if (value === null) {
      if (isAll) {
        setIsAllDomains(false);
        setSelectedDomains([]);
      } else {
        setIsAllDomains(true);
        setSelectedDomains([]);
      }
      return;
    }

    if (isAll) {
      setIsAllDomains(false);
      setSelectedDomains(allDomains.filter((domain) => domain !== value));
      return;
    }

    if (selectedDomains.includes(value)) {
      setSelectedDomains(selectedDomains.filter((domain) => domain !== value));
      return;
    }

    const nextDomains = [...selectedDomains, value];
    const isNextAllSelected = allDomains.every((domain) =>
      nextDomains.includes(domain),
    );

    if (isNextAllSelected) {
      setIsAllDomains(true);
      setSelectedDomains([]);
      return;
    }

    setSelectedDomains(nextDomains);
  };

  return (
    <S.OwnerEditContainer onClick={() => setIsDropbox(-1)}>
      {isSearch && (
        <MemberSearch
          setSelectedUser={setSelectedUser}
          onClose={() => setIsSearch(false)}
        />
      )}
      <S.OwnerEditPopup>
        <S.OwnerEditPopupHeader>
          <div>{owner ? "유저 권한 수정" : "유저 권한 추가"}</div>
        </S.OwnerEditPopupHeader>
        <S.OwnerEditInputWrapper>
          <S.OwnerEditInputItem>
            <div>유저</div>
            <input
              type="text"
              placeholder="유저를 선택하세요."
              value={selectedUser?.nickname}
              readOnly
              onClick={() => setIsSearch(true)}
            />
          </S.OwnerEditInputItem>
          <S.OwnerEditInputItem onClick={(e) => e.stopPropagation()}>
            <div>보유 권한</div>
            <S.OwnerEditDomainWrapper>
              {domainOptions.map((item) => (
                <S.OwnerEditDomainItem
                  key={item.value}
                  onClick={() => handleSelectDomain(item.value)}
                >
                  <S.OwnerEditCheckBox
                    selected={isDomainChecked(
                      item.value,
                      isAllDomains,
                      selectedDomains,
                    )}
                  >
                    <img
                      src={`/image-web/solution/un-selected.svg`}
                      alt="selected"
                    />
                  </S.OwnerEditCheckBox>
                  <span>{item.name}</span>
                </S.OwnerEditDomainItem>
              ))}
            </S.OwnerEditDomainWrapper>
          </S.OwnerEditInputItem>
          <S.OwnerEditInputItem onClick={(e) => e.stopPropagation()}>
            <div>토너먼트 접근 권한</div>
            <DropBox
              list={roleOptions}
              value={role}
              setValue={setRole}
              isOpen={isDropbox === 0}
              setIsOpen={(value) => setIsDropbox(value ? 0 : -1)}
            />
          </S.OwnerEditInputItem>
          {role === PubOwnerRole.STAFF && (
            <S.OwnerEditInputItem>
              <div>접근 가능 토너먼트 ID</div>
              <input
                type="text"
                placeholder="토너먼트 ID를 입력하세요. (1000,1001,1002...)"
                value={tournamentIds}
                onChange={(e) => setTournamentIds(e.target.value)}
              />
            </S.OwnerEditInputItem>
          )}
        </S.OwnerEditInputWrapper>
        <S.OwnerEditButtonWrapper>
          <div className="button" onClick={onClose}>
            취소
          </div>
          <div className="button confirm" onClick={handleSubmit}>
            확인
          </div>
        </S.OwnerEditButtonWrapper>
      </S.OwnerEditPopup>
    </S.OwnerEditContainer>
  );
};

export default OwnerEdit;
